import AppSimpleBar from "components/AppSimpleBar";
import { Utils } from "helpers/utils";
import { useRedux } from "hooks";
import { createSelector } from "reselect";
import Loader from "components/Loader";
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import * as urls from "../../../api/urls";
import "./ContentUploadSlip.css";
import { APIClient } from "api/apiCore";
import { UserTypes } from "api";

// --- Data Model ---
export interface SlipRecord {
  entry_id: number;
  upload_date: string;
  upload_datetime: string;
  group_chat_id: number;
  user_chat_id: number;
  company_id: number;
  file_original?: string;
  file_name: string;
  extension: string;
  size_byte: number;
  size_text: string;
  image_url: string;
  from_field_id?: number;
  from_msg_id?: number;
  hook_called: boolean;
  hook_count: number;
  hook_json: string;
  hook_error: string;
  qr_code?: string;
  ocr_text?: string;
  createby: number;
  updateby: number;
  createdate: string;
  updatedate: string;
  bank_to?: string;
  transaction_id?: string;
  bank_from?: string;
  amount?: string;
  transaction_date?: Date | null;
  note?: string;
}

interface Props {
  allowEdit: boolean;
  reloadKey?: number;
}

const coreSelector = createSelector(
  (state: any) => state.Chats,
  (state: any) => state.Profile2,
  (chats, profile) => ({
    selectedContentTitleLoading: chats.selectedContentTitleLoading,
    activeGroupId: profile.activeGroupId,
    userType: profile.userType,
    chatUserDetailsId: chats.chatUserDetails?.id,
  })
);

const ContentUploadSlipCore = ({ allowEdit, reloadKey }: Props) => {
  const { useAppSelector } = useRedux();
  const { selectedContentTitleLoading, activeGroupId, userType, chatUserDetailsId } = useAppSelector(coreSelector);

  const effectiveGroupId = userType === UserTypes.Employee ? chatUserDetailsId : activeGroupId;

  // --- Refs ---
  const activeGroupIdRef = useRef(effectiveGroupId);
  const lastLoadedGroupIdRef = useRef<any>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const slipsRef = useRef<SlipRecord[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scrollRef = useRef<any>(null);
  const previewWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { activeGroupIdRef.current = effectiveGroupId; }, [effectiveGroupId]);

  // --- States ---
  const [slips, setSlips] = useState<SlipRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [fileInfo, setFileInfo] = useState({ fileName: "upload.jpg", extension: "jpg" });
  const [formData, setFormData] = useState({
    bank_to: "",
    transaction_id: "",
    bank_from: "",
    amount: "",
    transaction_date: null as Date | null,
    note: "",
    qr_code: "",
    ocr_text: "",
  });
  const [capabilities, setCapabilities] = useState({
    hasCamera: false,
    hasClipboard: !!(navigator.clipboard && navigator.clipboard.read),
  });
  useEffect(() => {
    console.log('slips:', slips);
  }, [slips]);
  const [formError, setFormError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const [hasMoved, setHasMoved] = useState(false);

  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { slipsRef.current = slips; }, [slips]);

  // --- Image Dragging ---
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    setHasMoved(false);
    setDragStart({ x: e.pageX, y: e.pageY, scrollLeft: previewWrapperRef.current?.scrollLeft || 0, scrollTop: previewWrapperRef.current?.scrollTop || 0 });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || zoom <= 1) return;
    e.preventDefault();
    const walkX = e.pageX - dragStart.x;
    const walkY = e.pageY - dragStart.y;
    if (Math.abs(walkX) > 5 || Math.abs(walkY) > 5) setHasMoved(true);
    if (previewWrapperRef.current) {
      previewWrapperRef.current.scrollLeft = dragStart.scrollLeft - walkX;
      previewWrapperRef.current.scrollTop = dragStart.scrollTop - walkY;
    }
  };

  const handleMouseUpOrLeave = () => setIsDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoom <= 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setHasMoved(false);
    setDragStart({ x: touch.pageX, y: touch.pageY, scrollLeft: previewWrapperRef.current?.scrollLeft || 0, scrollTop: previewWrapperRef.current?.scrollTop || 0 });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || zoom <= 1) return;
    const touch = e.touches[0];
    const walkX = touch.pageX - dragStart.x;
    const walkY = touch.pageY - dragStart.y;
    if (Math.abs(walkX) > 5 || Math.abs(walkY) > 5) setHasMoved(true);
    if (previewWrapperRef.current) {
      previewWrapperRef.current.scrollLeft = dragStart.scrollLeft - walkX;
      previewWrapperRef.current.scrollTop = dragStart.scrollTop - walkY;
    }
  };

  const handleTouchEnd = () => setIsDragging(false);

  useEffect(() => {
    if (zoom <= 1 && previewWrapperRef.current) {
      previewWrapperRef.current.scrollLeft = 0;
      previewWrapperRef.current.scrollTop = 0;
    }
  }, [zoom]);

  // --- Device Capabilities ---
  useEffect(() => {
    const checkCapabilities = async () => {
      let hasCamera = false;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        hasCamera = devices.some(d => d.kind === 'videoinput');
      } catch (err) {
        console.warn("Camera detection failed", err);
      }
      setCapabilities({ hasCamera, hasClipboard: !!(navigator.clipboard && navigator.clipboard.read) });
    };
    checkCapabilities();
  }, []);

  // --- Fetch Data ---
  const fetchSlips = useCallback(async (isInitial = false) => {
    const currentGroupId = activeGroupIdRef.current;
    if (!currentGroupId) return;
    if (loadingRef.current || (!hasMoreRef.current && !isInitial)) return;

    setLoading(true);
    try {
      let lastUploadDatetime = null;
      if (!isInitial && slipsRef.current.length > 0) {
        lastUploadDatetime = slipsRef.current[slipsRef.current.length - 1].upload_datetime;
      }
      const payload = new FormData();
      payload.append("group_chat_id", currentGroupId.toString());
      if (lastUploadDatetime) payload.append("lastUploadDatetime", lastUploadDatetime);

      const response = await axios.post(urls.UPLOAD_SLIP_PAYMENT_LOAD, payload, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${Utils.JwtTokenGet()}` },
        withCredentials: true,
      });
      const newSlips = (response || []) as any;
      if (newSlips.length === 0) {
        setHasMore(false);
      } else {
        if (newSlips.length < 20) setHasMore(false);
        setSlips(prev => isInitial ? newSlips : [...prev, ...newSlips]);
      }
    } catch (error) {
      console.error("Error loading slips:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const isFirstReloadRef = useRef(true);
  useEffect(() => {
    if (isFirstReloadRef.current) { isFirstReloadRef.current = false; return; }
    setSlips([]);
    setHasMore(true);
    fetchSlips(true);
  }, [reloadKey, fetchSlips]);

  useEffect(() => {
    if (effectiveGroupId && effectiveGroupId !== 0 && effectiveGroupId !== "0" && effectiveGroupId !== lastLoadedGroupIdRef.current) {
      lastLoadedGroupIdRef.current = effectiveGroupId;
      setSlips([]);
      setHasMore(true);
      fetchSlips(true);
    }
  }, [effectiveGroupId, fetchSlips]);

  useEffect(() => {
    const scrollElem = scrollRef.current?.getScrollElement();
    if (!scrollElem) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElem;
      if (scrollHeight - scrollTop <= clientHeight + 100) fetchSlips();
    };
    scrollElem.addEventListener("scroll", handleScroll);
    return () => scrollElem.removeEventListener("scroll", handleScroll);
  }, [fetchSlips]);

  // --- Action Handlers ---
  const clearForm = () => {
    setFormData({ bank_to: "", transaction_id: "", bank_from: "", amount: "", transaction_date: null, note: "", qr_code: "", ocr_text: "" });
    setFormError(null);
  };

  const closeModal = () => {
    setShowPreview(false);
    setCurrentImage(null);
    setEditingId(null);
    setFileInfo({ fileName: "upload.jpg", extension: "jpg" });
    setZoom(1);
    clearForm();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileName = file.name;
      const extension = fileName.split('.').pop() || "jpg";
      setFileInfo({ fileName, extension });
      const reader = new FileReader();
      reader.onload = (event) => {
        clearForm();
        setEditingId(null);
        setZoom(1);
        setCurrentImage(event.target?.result as string);
        setShowPreview(true);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const handleClipboardPaste = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find(type => type.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const extension = imageType.split('/')[1] || "png";
          const fileName = `clipboard_${Date.now()}.${extension}`;
          setFileInfo({ fileName, extension });
          const reader = new FileReader();
          reader.onload = (event) => {
            clearForm();
            setEditingId(null);
            setZoom(1);
            setCurrentImage(event.target?.result as string);
            setShowPreview(true);
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      alert("ไม่พบรูปภาพใน Clipboard");
    } catch (err) {
      console.error("Clipboard access failed", err);
    }
  };

  const startCamera = async (facing: "environment" | "user" = facingMode) => {
    const existingStream = videoRef.current?.srcObject as MediaStream;
    existingStream?.getTracks().forEach(t => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing } } });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      console.error("Camera access failed", err);
      setShowCamera(false);
    }
  };

  const switchCamera = () => {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    startCamera(next);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg");
      (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      clearForm();
      setEditingId(null);
      setCurrentImage(dataUrl);
      setFileInfo({ fileName: `camera_${Date.now()}.jpg`, extension: "jpg" });
      setShowCamera(false);
      setShowPreview(true);
    }
  };

  const handleDelete = async () => {
    const confirmed = await Utils.SweetConfirm("ยืนยันการลบข้อมูลสลิปนี้?");
    if (!confirmed.isConfirmed) return;
    try {
      let error = '';
      if (editingId) {
        const api = new APIClient();
        const rs = await api.fnPostForm(urls.UPLOAD_SLIP_PAYMENT_DEL, { entry_id: editingId });
        error = rs.error;
        if (!error) setSlips(prev => prev.filter(s => s.entry_id !== editingId));
      }
      if (!error) {
        setShowPreview(false);
        setCurrentImage(null);
        setEditingId(null);
      } else {
        Utils.SweetAlert(error);
      }
    } catch (e) {
      Utils.HandleException(e, true);
    }
  };

  const handleRotate = async () => {
    if (!currentImage) return;
    try {
      const rotatedDataUrl = await new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.height;
            canvas.height = img.width;
            if (ctx) {
              ctx.translate(canvas.width / 2, canvas.height / 2);
              ctx.rotate((90 * Math.PI) / 180);
              ctx.drawImage(img, -img.width / 2, -img.height / 2);
              resolve(canvas.toDataURL("image/jpeg", 0.9));
            } else {
              reject(new Error("Canvas context not available"));
            }
          } catch (err) { reject(err); }
        };
        img.onerror = () => reject(new Error("Failed to load image for rotation"));
        img.src = currentImage;
      });
      setCurrentImage(rotatedDataUrl);
      setZoom(1);
    } catch (error) {
      console.error("Rotation error:", error);
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 4));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));


  const handleSubmit = async () => {
    setFormError(null);

    const currentGroupId = activeGroupIdRef.current;
    try {
      setLoading(true);
      const payload = new FormData();
      payload.append("entry_id", (editingId || 0).toString());
      payload.append("group_chat_id", currentGroupId.toString());
      payload.append("file_name", fileInfo.fileName);
      payload.append("extension", fileInfo.extension);
      payload.append("bank_to", formData.bank_to);
      payload.append("transaction_id", formData.transaction_id);
      payload.append("bank_from", formData.bank_from);
      payload.append("amount", formData.amount);
      payload.append("transaction_date", Utils.ToApiDateTimeString(formData.transaction_date));
      payload.append("note", formData.note);
      payload.append("qr_code", formData.qr_code);
      payload.append("ocr_text", formData.ocr_text);

      console.log('payload:', { payload, editingId });

      if (currentImage && currentImage.startsWith("data:")) {
        const response = await fetch(currentImage);
        const blob = await response.blob();
        payload.append("file", blob, fileInfo.fileName);
      }

      const uploadResponse = await axios.post(urls.UPLOAD_SLIP_PAYMENT, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${Utils.JwtTokenGet()}` // 20250708          
        },
        withCredentials: true // Optional, if you need to send cookies
      });
      // const uploadResponse = await axios.post(urls.UPLOAD_SLIP_PAYMENT, payload, {
      //   headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${Utils.JwtTokenGet()}` },
      //   withCredentials: true,
      // });

      const result = uploadResponse as any;

      if (editingId) {
        setSlips(prev => prev.map(s => s.entry_id === editingId ? { ...s, ...(result.data || formData) } : s));
      } else {
        // const newRecord: SlipRecord = result.data || {
        //   entry_id: Date.now(),
        //   upload_date: new Date().toISOString().split('T')[0],
        //   upload_datetime: new Date().toISOString(),
        //   group_chat_id: currentGroupId,
        //   user_chat_id: 1,
        //   company_id: 1,
        //   file_name: fileInfo.fileName,
        //   extension: fileInfo.extension,
        //   size_byte: 0,
        //   size_text: "Uploaded",
        //   image_url: currentImage || "",
        //   hook_called: false,
        //   hook_count: 0,
        //   hook_json: "",
        //   hook_error: "",
        //   createby: 1,
        //   updateby: 1,
        //   createdate: new Date().toISOString(),
        //   updatedate: new Date().toISOString(),
        //   ...formData,
        // };
        console.log('result:', result);
        setSlips(prev => [result, ...prev]);
      }

      setShowPreview(false);
      setCurrentImage(null);
      setEditingId(null);
      setFormData({ bank_to: "", transaction_id: "", bank_from: "", amount: "", transaction_date: null, note: "", qr_code: "", ocr_text: "" });
      setFileInfo({ fileName: "upload.jpg", extension: "jpg" });
      setZoom(1);
      setFormError(null);
    } catch (error) {
      Utils.HandleException(error, true);
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const isDark = Utils.IsDarkTheme();

  return (
    <>
      {allowEdit && (
        <div className="fab-container">
          <button className="fab-button" onClick={() => fileInputRef.current?.click()}>
            <i className="bx bx-upload font-size-24"></i>
            <span className="fab-label">อัปโหลดรูป</span>
          </button>
          {capabilities.hasCamera && (
            <button className="fab-button" onClick={() => { setShowCamera(true); startCamera(); }}>
              <i className="bx bx-camera font-size-24"></i>
              <span className="fab-label">ถ่ายรูป</span>
            </button>
          )}
          {capabilities.hasClipboard && (
            <button className="fab-button" onClick={handleClipboardPaste}>
              <i className="bx bx-paste font-size-24"></i>
              <span className="fab-label">วางจากการคัดลอก</span>
            </button>
          )}
        </div>
      )}

      {allowEdit && (
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileUpload} />
      )}

      <AppSimpleBar className="p-3 p-md-4 positin-relative content-list-ul" scrollRef={scrollRef}>
        {selectedContentTitleLoading && <Loader />}
        <div className="slip-list">
          {slips.map((slip) => (
            <div key={slip.entry_id} className="slip-item" onClick={() => {
              setEditingId(slip.entry_id);
              setZoom(1);
              setCurrentImage(slip.image_url);
              setFileInfo({ fileName: slip.file_name, extension: slip.extension });
              setFormData({
                bank_to: slip.bank_to || "",
                transaction_id: slip.transaction_id || "",
                bank_from: slip.bank_from || "",
                amount: slip.amount || "",
                transaction_date: Utils.ConvertApiDate(slip.transaction_date) || null,
                note: slip.note || "",
                qr_code: slip.qr_code || "",
                ocr_text: slip.ocr_text || "",
              });
              setShowPreview(true);
            }}>
              <div className="slip-item-img-wrap">
                <img src={slip.image_url} alt="slip" className="slip-item-img" />
                <div className="slip-date-badge">{Utils.GetDMYHHMM(Utils.ConvertApiDate(slip.upload_datetime))}</div>
              </div>
            </div>
          ))}
          {loading && <div className="text-center p-3"><Loader /></div>}
        </div>
      </AppSimpleBar>

      {showPreview && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h5 className="mb-0">ตรวจสอบข้อมูลสลิป {editingId}</h5>
              <button className="btn-close ms-auto" onClick={closeModal} disabled={loading}></button>
            </div>
            <div className="modal-body">
              <div className="preview-image-section">
                <div className="zoom-controls">
                  <button className="btn btn-sm btn-light" onClick={handleZoomIn}><i className="bx bx-zoom-in"></i></button>
                  <button className="btn btn-sm btn-light" onClick={() => setZoom(1)}>{(zoom * 100).toFixed(0)}%</button>
                  <button className="btn btn-sm btn-light" onClick={handleZoomOut}><i className="bx bx-zoom-out"></i></button>
                </div>
                <div className="preview-image-wrapper" ref={previewWrapperRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUpOrLeave} onMouseLeave={handleMouseUpOrLeave} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
                  <img
                    src={currentImage || ""}
                    alt="Preview"
                    className="preview-image"
                    style={{
                      ...(zoom > 1 ? { width: `${zoom * 100}%`, maxWidth: 'none', height: 'auto' } : {}),
                      cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                    }}
                    onClick={() => { if (hasMoved) { setHasMoved(false); return; } zoom > 1 ? setZoom(1) : setZoom(2); }}
                    draggable={false}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ flexWrap: "wrap", gap: "8px" }}>
              <div className="me-auto">
                {allowEdit && (
                  <button className="btn btn-info me-2" onClick={handleRotate} disabled={loading}>
                    <i className="bx bx-rotate-right font-size-16 align-middle me-1"></i>หมุนรูป
                  </button>
                )}
{allowEdit && editingId && (
                  <button className="btn btn-danger" onClick={handleDelete} disabled={loading}>ลบ</button>
                )}
              </div>
              {formError && (
                <div className="w-100 text-danger text-end" style={{ fontSize: "0.85rem" }}>
                  <i className="bx bx-error-circle me-1"></i>{formError}
                </div>
              )}
              <button className="btn btn-secondary" onClick={closeModal} disabled={loading}>ยกเลิก</button>
              {allowEdit && (
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading && <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>}บันทึก
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCamera && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h5 className="mb-0">ถ่ายรูปสลิป</h5>
              <button className="btn-close" onClick={() => { (videoRef.current?.srcObject as MediaStream)?.getTracks().forEach(t => t.stop()); setShowCamera(false); }}></button>
            </div>
            <div className="modal-body p-0">
              <div className="camera-container">
                <video ref={videoRef} autoPlay playsInline className="camera-video"></video>
                <div className="camera-controls">
                  <button className="capture-btn" onClick={capturePhoto}></button>
                  <button
                    onClick={switchCamera}
                    style={{ position: "absolute", top: "12px", right: "12px", background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: "40px", height: "40px", color: "#fff", fontSize: "20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    title={facingMode === "environment" ? "สลับกล้องหน้า" : "สลับกล้องหลัง"}
                  >
                    <i className="bx bx-revision"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContentUploadSlipCore;
